<script setup lang="ts">
import { gaiaMotion, gaiaNeutral, gaiaShadow } from '@/components/design/themes/tokens';
import FieldEntryPane from './FieldEntryPane.vue';
import LivePreviewPanel from './LivePreviewPanel.vue';
import type { TemplateSchema } from '@/api';
import type { TemplateFieldMetadataMap } from '@/types/contractGeneration/wizard';

/**
 * The compose workspace: the document on the right, as large as the screen
 * allows, and a fixed-width answer panel on the left that walks the user
 * through the template one section at a time.
 *
 * Drafting is reading a contract while answering questions about it, so the
 * document gets the width and the questions get a column. Both panes scroll
 * inside themselves — the page never scrolls — which keeps the document and
 * the current question in view together, and lets the change highlight land
 * where the eye already is.
 */

interface Props {
  schema: TemplateSchema;
  /** Authored labels/guidance per field path — read by the answer panel only. */
  fieldMetadata?: TemplateFieldMetadataMap | null;
  formData: Record<string, unknown>;
  showValidation: boolean;
  variantUid: string;
  suggestedFileName: string;
  disabled?: boolean;
  /** Whether the live-preview panel is collapsed. Controlled by the wizard. */
  collapsed?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  fieldMetadata: null,
  disabled: false,
  collapsed: false,
});
const emit = defineEmits<{
  (e: 'update:formData', value: Record<string, unknown>): void;
  (e: 'update:collapsed', value: boolean): void;
}>();

function collapseFromPreviewHeader(): void {
  if (!props.collapsed) emit('update:collapsed', true);
}

function handleFormUpdate(value: Record<string, unknown>): void {
  emit('update:formData', value);
}

// --- chrome (tokens for the scoped styles) ---
const gBorder = gaiaNeutral.borderCard;
const gSurface = gaiaNeutral.surface;
const gCardShadow = gaiaShadow.card;
const settle = gaiaMotion.settle;
const dSlow = gaiaMotion.slow;
</script>

<template>
  <div class="compose" :class="{ 'compose--document-hidden': collapsed }">
    <!-- The answer panel. A fixed measure: wide enough for a name or an
         address, narrow enough that the document keeps the screen. With the
         document hidden it becomes a centred form instead. -->
    <section class="compose__fields" data-tour="tpl.compose-form">
      <FieldEntryPane
        :schema="schema"
        :field-metadata="fieldMetadata"
        :form-data="formData"
        :show-validation="showValidation"
        :disabled="disabled"
        @update:form-data="handleFormUpdate"
      />
    </section>

    <aside v-if="!collapsed" class="compose__document" data-tour="tpl.preview">
      <LivePreviewPanel
        :variant-uid="variantUid"
        :schema="schema"
        :form-data="formData"
        :suggested-file-name="suggestedFileName"
        :disabled="disabled"
        @collapse="collapseFromPreviewHeader"
      />
    </aside>
  </div>
</template>

<style scoped>
/* Two full-height panes side by side. The fill chain above hands this box the
   column's height; `min-height: 0` on each pane is what lets them shrink to
   it and scroll inside instead of growing the page. No component expresses a
   fixed-measure pane beside a fluid one, so the geometry is CSS. */
.compose {
  flex: 1 1 auto;
  display: flex;
  gap: 16px;
  min-height: 0;
  min-width: 0;
}

.compose__fields {
  flex: 0 0 400px;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid v-bind(gBorder);
  border-radius: 12px;
  background: v-bind(gSurface);
  box-shadow: v-bind(gCardShadow);
  overflow: hidden;
  animation: compose-pane-in v-bind(dSlow) v-bind(settle) both;
}

/* The two panes settle in on first paint, the document a beat after the
   fields, so the workspace assembles rather than appears. */
@keyframes compose-pane-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .compose__fields,
  .compose__document {
    animation: none;
  }
}

/* Form only: the panel takes the reading measure and centres itself. */
.compose--document-hidden .compose__fields {
  flex: 1 1 auto;
  max-width: 720px;
  margin: 0 auto;
}

.compose__document {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  border: 1px solid v-bind(gBorder);
  border-radius: 12px;
  background: v-bind(gSurface);
  box-shadow: v-bind(gCardShadow);
  overflow: hidden;
  animation: compose-pane-in v-bind(dSlow) v-bind(settle) 80ms both;
}

/* Narrow screens stack the panes and let the page scroll: the document keeps
   a readable height of its own rather than shrinking into a sliver. */
@media (max-width: 1024px) {
  .compose {
    flex-direction: column;
    overflow: auto;
  }

  .compose__fields {
    flex: none;
    max-height: none;
  }

  .compose__document {
    flex: none;
    height: 70vh;
  }
}
</style>
